<template>
  <!--
    Native-style mobile home screen (rendered by Dashboard.vue when `isPhone`).
    A greeting, a tap-friendly shortcut grid built from the shared nav config,
    and a floating Quick Actions button — purpose-built for phones rather than
    the shrunk desktop widget stack.
  -->
  <div class="mobile-home">
    <!-- Greeting / identity header -->
    <div class="greeting">
      <div class="text-caption text--secondary">{{ today }}</div>
      <div class="text-h5 font-weight-bold mt-1">Hi, {{ firstName }} 👋</div>
      <div class="d-flex align-center mt-1">
        <v-icon x-small class="mr-1">mdi-office-building-outline</v-icon>
        <span class="text-body-2 text--secondary">{{ branchName }}</span>
      </div>
    </div>

    <!-- Shortcut grid -->
    <div class="px-3 pb-8">
      <div class="section-label px-1 mt-4 mb-2">Quick access</div>
      <v-row dense>
        <v-col
          v-for="link in shortcuts"
          :key="link.route"
          cols="4"
        >
          <v-card
            flat
            rounded="xl"
            class="shortcut-card"
            :to="link.route"
            ripple
          >
            <v-avatar size="46" class="shortcut-icon mb-2">
              <v-icon color="primary">{{ link.icon }}</v-icon>
            </v-avatar>
            <div class="shortcut-label">{{ link.name }}</div>
          </v-card>
        </v-col>
      </v-row>

      <div v-if="!shortcuts.length" class="text-center text--secondary mt-10">
        <v-icon size="48" class="mb-2">mdi-lock-outline</v-icon>
        <div class="text-body-2">No sections are available for your account.</div>
      </div>
    </div>

    <!-- Floating Quick Actions -->
    <v-btn
      fab
      fixed
      bottom
      right
      color="primary"
      class="home-fab"
      @click="openQuickActions"
    >
      <v-icon>mdi-lightning-bolt</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { visibleNavLinks } from "@/config/navLinks.js";

export default {
  name: "MobileHome",
  computed: {
    ...mapGetters({
      user: "auth/user",
      hasPermission: "permission/hasPermission",
    }),
    firstName() {
      const name = (this.user && this.user.username) || "there";
      return name.split(" ")[0];
    },
    branchName() {
      return (this.user && this.user.branch && this.user.branch.branch_name) || "";
    },
    today() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    },
    // Every destination the user can reach, minus Dashboard itself (they're
    // already home), surfaced as shortcut tiles.
    shortcuts() {
      return visibleNavLinks(this.user, this.hasPermission).filter(
        (l) => l.route !== "/"
      );
    },
  },
  methods: {
    openQuickActions() {
      this.$root.$emit("open-quick-actions");
    },
  },
};
</script>

<style scoped>
.mobile-home {
  padding-bottom: 72px;
}
.greeting {
  padding: 20px 20px 4px;
}

.shortcut-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px 6px 12px;
  min-height: 104px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: transform 0.08s ease;
}
.shortcut-card:active {
  transform: scale(0.96);
}
.shortcut-icon {
  background: rgba(25, 118, 210, 0.1);
}
.shortcut-label {
  font-size: 0.72rem;
  line-height: 1.15;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.78);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.home-fab {
  bottom: 72px !important; /* clear the bottom tab bar */
  margin-bottom: env(safe-area-inset-bottom);
}

/* Dark theme */
.theme--dark .shortcut-card {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
}
.theme--dark .shortcut-icon {
  background: rgba(25, 118, 210, 0.22);
}
.theme--dark .shortcut-label {
  color: rgba(255, 255, 255, 0.86);
}
</style>
